// Web前端安全加密调用
// 通过API调用服务器端加密/解密服务

/**
 * 通过服务器API加密用户数据
 * @param {Object} data - 要加密的数据
 * @returns {Promise<Object>} 加密后的数据对象
 */
async function encryptUserData(data) {
  try {
    const response = await fetch('/api/encryption/encrypt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-client-id': 'web'
      },
      body: JSON.stringify({ 
        data: data, 
        clientId: 'web' 
      })
    });
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || '加密失败');
    }
    
    return {
      data: result.data,
      key: result.key,
      iv: result.iv
    };
  } catch (error) {
    console.error('Web加密调用失败:', error);
    throw error;
  }
}

/**
 * 通过服务器API解密用户数据
 * @param {Object|string} encryptedResponse - 加密的响应数据
 * @returns {Promise<Object>} 解密后的数据
 */
async function decryptUserData(encryptedResponse) {
  try {
    // 检查必要参数
    console.log('decryptUserData 接收到的参数:', encryptedResponse);
    if (!encryptedResponse) {
      console.error('缺少必要的解密参数: encryptedResponse 为 null 或 undefined');
      throw new Error('缺少必要的解密参数');
    }
    
    // 如果 encryptedResponse 是字符串，则解析为对象
    let responseData = encryptedResponse;
    if (typeof encryptedResponse === 'string') {
      try {
        responseData = JSON.parse(encryptedResponse);
        console.log('解析后的数据:', responseData);
      } catch (parseError) {
        console.error('无法解析 encryptedResponse 字符串:', encryptedResponse);
        throw new Error('无法解析加密数据');
      }
    }
    
    // 检查解析后的数据是否包含 data, key, iv 字段
    console.log('检查字段存在性:', {
      hasData: 'data' in responseData,
      hasKey: 'key' in responseData,
      hasIv: 'iv' in responseData,
      dataType: typeof responseData.data,
      keyType: typeof responseData.key,
      ivType: typeof responseData.iv
    });
    
    // 检查 responseData 是否包含 data, key, iv 字段
    if (responseData.data && responseData.key && responseData.iv) {
      // 直接使用这些字段
      const response = await fetch('/api/encryption/decrypt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-id': 'web'
        },
        body: JSON.stringify({
          encryptedData: responseData.data,
          key: responseData.key,
          iv: responseData.iv,
          clientId: 'web'
        })
      });
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || '解密失败');
      }
      
      return result.data;
    } else {
      console.error('缺少必要的解密参数: responseData 不包含有效的 data, key, iv 字段', responseData);
      throw new Error('缺少必要的解密参数: 无法提取加密数据');
    }
  } catch (error) {
    console.error('Web解密调用失败:', error);
    throw error;
  }
}

/**
 * Base64字符串转ArrayBuffer
 * @param {string} base64 - Base64编码的字符串
 * @returns {ArrayBuffer} ArrayBuffer对象
 */
function base64ToArrayBuffer(base64) {
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0)).buffer;
}

/**
 * ArrayBuffer转Base64字符串
 * @param {ArrayBuffer} arrayBuffer - ArrayBuffer对象
 * @returns {string} Base64编码的字符串
 */
function arrayBufferToBase64(arrayBuffer) {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(arrayBuffer)));
}

// 导出函数供前端使用
window.encryptUserData = encryptUserData;
window.decryptUserData = decryptUserData;

// 同时使用模块系统导出，以支持不同导入方式
try {
  if (module && module.exports) {
    module.exports = {
      encryptUserData,
      decryptUserData,
      base64ToArrayBuffer,
      arrayBufferToBase64
    };
  }
} catch (e) {
  // 在浏览器环境中，module可能未定义，忽略错误
}

// 默认导出
export { 
  encryptUserData, 
  decryptUserData,
  base64ToArrayBuffer,
  arrayBufferToBase64
};

export default {
  encryptUserData,
  decryptUserData,
  base64ToArrayBuffer,
  arrayBufferToBase64
};
